import maplibregl, { type Map } from 'maplibre-gl';
import type { DataSource } from '../data/source';
import type { POI } from '../domain/types';

const SOURCE_ID = 'poi';
const CIRCLES_LAYER = 'poi-circles';
const LABELS_LAYER = 'poi-labels';

function poisToGeoJSON(pois: POI[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: pois.map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.coords.lng, p.coords.lat] },
      properties: {
        id: p.id,
        name: p.name,
        type: p.type,
        description: p.description ?? '',
      },
    })),
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export async function addPOILayer(map: Map, dataSource: DataSource): Promise<void> {
  const pois = await dataSource.getPOIs();
  const data = poisToGeoJSON(pois);

  map.addSource(SOURCE_ID, { type: 'geojson', data });

  map.addLayer({
    id: CIRCLES_LAYER,
    type: 'circle',
    source: SOURCE_ID,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 14, 7, 17, 10],
      'circle-color': [
        'match',
        ['get', 'type'],
        'belvedere', '#1e88e5',
        'rifugio', '#8d4e2a',
        'chiesa', '#5a4a4a',
        'fontana', '#00acc1',
        'parcheggio', '#1565c0',
        'punto_sosta', '#43a047',
        'rudere', '#6e6357',
        '#999999',
      ],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  });

  map.addLayer({
    id: LABELS_LAYER,
    type: 'symbol',
    source: SOURCE_ID,
    minzoom: 14,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Medium'],
      'text-size': 12,
      'text-offset': [0, 1.2],
      'text-anchor': 'top',
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#1a1a1a',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.5,
    },
  });

  map.on('click', CIRCLES_LAYER, (e) => {
    const feature = e.features?.[0];
    if (!feature) return;
    const props = (feature.properties ?? {}) as Record<string, string>;
    const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];

    const html = `
      <div style="font-family: system-ui, sans-serif; font-size: 14px; min-width: 180px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${escapeHtml(props.name ?? '')}</div>
        <div style="color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">
          ${escapeHtml(props.type ?? '')}
        </div>
        ${props.description ? `<div style="margin-top: 6px; color: #333;">${escapeHtml(props.description)}</div>` : ''}
      </div>
    `;

    new maplibregl.Popup({ offset: 14, closeButton: true })
      .setLngLat(coords)
      .setHTML(html)
      .addTo(map);
  });

  map.on('mouseenter', CIRCLES_LAYER, () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', CIRCLES_LAYER, () => {
    map.getCanvas().style.cursor = '';
  });
}
